import React, {useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ToolTipCell} from 'components/ToolTipCell';
import {ExitModal} from '../../../Modals';
import {useTracking} from '../../../../services/analytics/TrackingContext';

const absoluteLink = /^https?:\/\//i;

/**
 * Renders the documents table column. Each `column.meta.documents` entry may include
 * `unavailableText` — hover tooltip when the document is missing or the source is not a valid
 * http(s) link (and `showOnlyFound` is false).
 */
export const DocumentsCell = ({settings, styleSheet, value, column, rowData}) => {
  const {trackResultClick, trackExternalLinkExitModal} = useTracking();
  const [showExitModal, setShowExitModal] = useState(false);
  const [documentLink, setDocumentLink] = useState('');

  const handleDocumentClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.href;
    setDocumentLink(href);
    setShowExitModal(true);
    const clickType = e.currentTarget.dataset.trackClickType;
    if (clickType && rowData) {
      trackResultClick(clickType, rowData);
    }
  };

  const handleOpenDocument = () => {
    trackExternalLinkExitModal('leaves');
    window.open(documentLink, '_blank', 'noopener,noreferrer');
    setShowExitModal(false);
  };

  const handleHide = () => {
    trackExternalLinkExitModal('stays');
    setShowExitModal(false);
  };

  const defaultDocumentMeta = {
    name: 'Coverage Document',
    label: 'Coverage Document',
    showOnlyFound: false,
    unavailableText: 'Coverage document not available',
    icon: {
      prefix: 'far',
      name: 'file',
    },
  };

  // Track documents that have already been assigned to prevent duplicates
  const usedDocuments = new Set();

  return (
    <>
      <div>{
        column.meta.documents.map((documentMeta, key) => {
          //Find document by matching document type name in the documentMeta object
          let document = value.find((doc) => {
            return !usedDocuments.has(doc) && doc.documentTypes.some((type) => type.name === documentMeta.name);
          });

          //If not found, find primary document
          if (!document && documentMeta.name === defaultDocumentMeta.name) {
            document = value.find((doc) => {
              return !usedDocuments.has(doc) && doc.isPrimary === true;
            });
          }

          // Exclude documents whose types are in excludeDocuments list
          if (document && documentMeta.excludeDocuments?.length > 0 && document.documentTypes) {
            const isExcluded = document.documentTypes.some((type) =>
              documentMeta.excludeDocuments.includes(type.name)
            );
            if (isExcluded) {
              document = null;
            }
          }

          //If primary document is not found and showOnlyFound is true, return null
          if (!document && documentMeta.showOnlyFound) {
            return null;
          }

          // Mark document as used to prevent duplicate assignments
          if (document) {
            usedDocuments.add(document);
          }

          // Merge defaultDocumentMeta only if documentMeta is missing properties
          if (!documentMeta.label) {
            documentMeta = {...defaultDocumentMeta, ...documentMeta};
          }

          // If no icon is specified, use default
          if (!documentMeta.icon) {
            documentMeta.icon = defaultDocumentMeta.icon;
          }

          const icon = findIconDefinition({prefix: documentMeta.icon.prefix, iconName: documentMeta.icon.name});
          return (
            <React.Fragment key={`${key}-${documentMeta.label}`}>
              {document && document.documentTypes.length > 0 && absoluteLink.test(document.source) ? (
                <a
                  onClick={handleDocumentClick}
                  href={document.source}
                  rel='noopener noreferrer'
                  data-track-click-type={documentMeta.name === 'Coverage Document' ? 'coverage_document' : documentMeta.name === 'Prior Authorization (PA) Form' || documentMeta.name === 'PA Form' ? 'pa_form' : 'coverage_document'}
                  {...(column.meta?.contextAriaLabel && rowData && {
                    'aria-label': `${documentMeta.label} for ${rowData.payer} ${rowData.planType}`
                  })}
                >
                  <FontAwesomeIcon icon={icon}/>{documentMeta.label}
                </a>
              ) : (
                !documentMeta.showOnlyFound ? (
                  <ToolTipCell
                    placement={'bottom'}
                    styleSheet={styleSheet}
                    text={documentMeta.unavailableText}
                    placeholder={(<span><FontAwesomeIcon icon={icon} />{documentMeta.label}</span>)}
                    id={`doc-tooltip-${key}`}
                  />
                ) : (
                  <span></span>
                )
              )}
              <br/>
            </React.Fragment>
          );
        })
      }</div>
      <ExitModal
        settings={settings}
        styleSheet={styleSheet}
        showExitModal={showExitModal}
        handleHide={handleHide}
        handleOpenDocument={handleOpenDocument}
      />
    </>
  );
};

